import { Link } from "react-router-dom"
import {
  useGetOrderReviewQuery,
  useGetGroupOrderQuery,
} from "shared/redux/endpoints/procurementApprovals"
import {
  Drawer,
  StatusPill,
  MoneyText,
  DateText,
  EmptyState,
} from "procurement/design"
import { Loading } from "../../ui"
import { DecisionButtons, allowed } from "../approvals/DecisionCard"
import en from "procurement/design/i18n/en"

const OrderQuickDrawer = ({
  orderId,
  groupId,
  open,
  onClose,
  fullPath,
  onAction,
}) => {
  const { data: order, isFetching: orderLoading } = useGetOrderReviewQuery(orderId, {
    skip: !open || !orderId,
  })
  const { data: group, isFetching: groupLoading } = useGetGroupOrderQuery(groupId, {
    skip: !open || !groupId,
  })
  const record = orderId ? order : group
  const loading = orderId ? orderLoading : groupLoading
  const actions = allowed(record?.allowedActions || [])

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        record ? (
          <div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{record.code || record.orCode}</div>
            <h2 className="pc-h1" style={{ fontSize: 18 }}>
              {record.title || en.orders.drawerTitle}
            </h2>
          </div>
        ) : (
          en.orders.drawerTitle
        )
      }
      footer={
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {onAction && actions.length > 0 && (
            <DecisionButtons actions={actions} onPick={(a) => onAction(record, a)} block={false} />
          )}
          {fullPath && (
            <Link to={fullPath} className="pc-btn pc-btn--primary pc-btn--sm">
              {en.common.openFullPage}
            </Link>
          )}
        </div>
      }
    >
      {loading && <Loading />}
      {!loading && !record && (
        <EmptyState title="Couldn't load this record" text="Open the full page or try again." />
      )}
      {!loading && record && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <StatusPill status={record.status} />
            <MoneyText value={record.total ?? record.totalAmount} align="left" />
          </div>
          <dl style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, margin: 0, fontSize: 13 }}>
            <div>
              <dt style={{ color: "var(--text-muted)", fontSize: 12 }}>Requester</dt>
              <dd style={{ margin: 0 }}>{record.requesterName || "—"}</dd>
            </div>
            <div>
              <dt style={{ color: "var(--text-muted)", fontSize: 12 }}>Category</dt>
              <dd style={{ margin: 0 }}>{record.categoryName || "—"}</dd>
            </div>
            <div>
              <dt style={{ color: "var(--text-muted)", fontSize: 12 }}>Last updated</dt>
              <dd style={{ margin: 0 }}>
                <DateText value={record.updatedAt || record.createdAt} withTime />
              </dd>
            </div>
            <div>
              <dt style={{ color: "var(--text-muted)", fontSize: 12 }}>Region</dt>
              <dd style={{ margin: 0 }}>{record.region || "—"}</dd>
            </div>
          </dl>

          <section>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <h3 style={{ fontSize: 13, margin: 0 }}>Vendor proposals</h3>
              {orderId && (
                <Link
                  to={`/procurement/order-requests/${orderId}/vendor-proposals`}
                  className="pc-btn pc-btn--ghost pc-btn--sm"
                >
                  Manage →
                </Link>
              )}
            </div>
            {!!record.vendorProposals?.length &&
              record.vendorProposals.map((v, i) => (
                <div key={v.id || i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "6px 0", borderTop: "1px solid var(--border)" }}>
                  <span>{v.supplierName || v.vendorName || "—"}</span>
                  <MoneyText value={v.totalAmount ?? v.amount} />
                </div>
              ))}
          </section>

          {!!record.approvalProgress?.length && (
            <section>
              <h3 style={{ fontSize: 13, margin: "0 0 8px" }}>Approval progress</h3>
              {record.approvalProgress.map((step, i) => (
                <div key={step.stepNo || i} style={{ fontSize: 13, padding: "6px 0", borderTop: "1px solid var(--border)" }}>
                  Step {step.stepNo ?? i + 1} · {step.approverName || step.approverRole || "—"} ·{" "}
                  <StatusPill status={step.status || step.result} />
                </div>
              ))}
            </section>
          )}

          {!!(record.actionLog || record.timeline)?.length && (
            <section>
              <h3 style={{ fontSize: 13, margin: "0 0 8px" }}>Timeline</h3>
              {(record.actionLog || record.timeline).map((ev, i) => (
                <div key={ev.id || i} style={{ fontSize: 13, padding: "6px 0", borderTop: "1px solid var(--border)" }}>
                  <DateText value={ev.createdAt || ev.at} relative /> · {ev.action || ev.note || ev.message || "—"}
                </div>
              ))}
            </section>
          )}
        </div>
      )}
    </Drawer>
  )
}

export default OrderQuickDrawer
